/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { useCallback, useMemo, useState } from "react";
import { CookieDetail, useConsent } from "../utils/compliance";

/**
 * React state backed by session storage if functional cookie consent has been given.
 */
export function useStorageWithConsent<T>(
  { type, category, name }: CookieDetail,
  defaultValueFactory: () => T
): [T | undefined, (value: T) => void] {
  if (type === "cookie") {
    throw new Error(
      "Type 'cookie' cannot be used for local or session storage."
    );
  }
  const consent = useConsent();
  const [state, setState] = useState<T | undefined>(() => {
    // Fine to read from storage. If consent is not given, storage is cleared.
    const storage =
      type === "local" ? localStorageIfPossible() : sessionStorageIfPossible();
    const value = storage ? storage.getItem(name) : null;
    if (value !== null) {
      try {
        let parsed = JSON.parse(value);
        return parsed as T;
      } catch (e) {
        // Better than exploding forever.
        return defaultValueFactory();
      }
    }
    return defaultValueFactory();
  });

  const consentCheckPassed = useMemo(() => {
    if (!consent) {
      return false;
    }
    if (category === "essential") {
      return true;
    }
    return consent[category];
  }, [category, consent]);

  const setAndSaveState = useCallback(
    (value: T) => {
      // In practice this is in response to user actions that cannot be triggered until there
      // is consent.
      const storage = consentCheckPassed
        ? type === "local"
          ? localStorageIfPossible()
          : sessionStorageIfPossible()
        : undefined;
      if (storage) {
        storage.setItem(name, JSON.stringify(value));
      }
      setState(value);
    },
    [consentCheckPassed, name, type]
  );
  return [state, setAndSaveState];
}

const localStorageIfPossible = () => {
  try {
    return window.localStorage;
  } catch (e) {
    // Handle possible SecurityError, absent window.
    return undefined;
  }
};

const sessionStorageIfPossible = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    // Handle possible SecurityError, absent window.
    return undefined;
  }
};
